// ============================================================================
// navGuard — one place a screen can say "not yet" to a tab change
// ============================================================================
// Portal navigation in this app funnels through useUiStore.setTab (the header
// nav and the drawer both call it — see App.js onPortalNav / handleSubsectionClick),
// so setTab is the single choke point for "the user is leaving". This registry
// lets ONE screen interpose there without every nav call site learning about it,
// and without a navigation refactor.
//
// Deliberately NOT a React context: setTab lives in the Zustand store, outside
// the tree, so a context would not be reachable from it.
//
// Contract: a guard returns true if it INTERCEPTED (it now owns the decision and
// must eventually call `proceed`), false to let navigation happen normally. Only
// one guard is registered at a time — the screen that holds unsaved work.
// ============================================================================

let guardFn = null;

/**
 * Register the active guard. Returns an unregister function; call it when the
 * screen no longer has anything to protect (clean edits, or unmount).
 */
export function registerNavGuard(fn) {
  guardFn = fn;
  return () => { if (guardFn === fn) guardFn = null; };
}

/**
 * Ask the guard for permission to navigate.
 * @returns true when intercepted (caller must NOT navigate), false to proceed.
 */
export function requestNavigation(proceed) {
  if (typeof guardFn !== 'function') return false;
  try {
    return guardFn(proceed) === true;
  } catch {
    // A broken guard must never trap the user in the app (§2.3 in spirit).
    return false;
  }
}
